import type { FerryBooking, FerryCarrierBookingStatus } from '@my/ferry-api';

/**
 * What a Ferry booking means for a TMS row. All fourteen carrier statuses are
 * mapped so a carrier beyond Stena needs no change here.
 *
 *  • Confirmed — the carrier holds the booking (any stage from confirmed to released).
 *  • Booked    — placed, not yet confirmed: waitlisted, or our call still in flight.
 *  • Alert     — refused or gone wrong; `message` says why.
 */
export type FerryRowStatus = 'Confirmed' | 'Booked' | 'Alert';

const CONFIRMED_LIKE: ReadonlySet<FerryCarrierBookingStatus> = new Set([
  'Confirmed',
  'CheckedIn',
  'CheckedInWaiter',
  'CustomsCleared',
  'Loaded',
  'Shipped',
  'Arrived',
  'Discharged',
  'Released',
]);

const ALERT_LIKE: ReadonlySet<FerryCarrierBookingStatus> = new Set([
  'Rejected',
  'NoShow',
  'Cancelled',
]);

export function ferryRowStatus(
  booking: Pick<FerryBooking, 'carrierStatus' | 'integrationStatus'>,
): FerryRowStatus {
  if (booking.integrationStatus === 'Failed') return 'Alert';
  if (ALERT_LIKE.has(booking.carrierStatus)) return 'Alert';
  if (booking.integrationStatus === 'Completed' && CONFIRMED_LIKE.has(booking.carrierStatus)) {
    return 'Confirmed';
  }
  return 'Booked';
}

/** True once the carrier has accepted the booking, which is when a cancel has to go to the carrier too. */
export function ferryBookingAccepted(
  booking: Pick<FerryBooking, 'carrierBookingNumber' | 'carrierStatus'>,
): boolean {
  return booking.carrierBookingNumber != null && booking.carrierStatus !== 'Cancelled';
}

/** Short operator-facing text for the carrier's status, e.g. `CheckedInWaiter` → "Checked in (waiter)". */
export function ferryCarrierStatusLabel(status: FerryCarrierBookingStatus): string {
  switch (status) {
    case 'CheckedIn':
      return 'Checked in';
    case 'CheckedInWaiter':
      return 'Checked in (waiter)';
    case 'NoShow':
      return 'No show';
    case 'CustomsCleared':
      return 'Customs cleared';
    default:
      return status;
  }
}
